import { feuilleArrondis, normaliserArrondis, type ArrondisPlateforme } from './arrondis'
import { feuilleBoutons, normaliserBoutons, type BoutonsPlateforme } from './boutons'
import { feuilleDensite, normaliserDensite, type DensitePlateforme } from './densite'
import { feuilleOmbres, normaliserOmbres, type OmbresPlateforme } from './ombres'
import { feuilleDeStyle, normaliser, type CouleursPlateforme } from './palette'
import { normaliserEntete, type EntetePlateforme } from './entete'
import { feuillePolices, normaliserPolices, type PolicesPlateforme } from './polices'
import { normaliserSections, type SectionsPlateforme } from './sections'

/**
 * L'ensemble des réglages d'apparence — couleurs, arrondis, polices, boutons,
 * densité, ombres, barre du haut, en-têtes de section de fiche —, tels que les échange l'API et les garde le
 * cache local. Pur, comme ses composantes.
 */
export interface ReglagesApparence {
  colors: CouleursPlateforme
  radii: ArrondisPlateforme
  fonts: PolicesPlateforme
  buttons: BoutonsPlateforme
  density: DensitePlateforme
  shadows: OmbresPlateforme
  header: EntetePlateforme
  sections: SectionsPlateforme
}

export const REGLAGES_VIDES: ReglagesApparence = {
  colors: { light: {}, dark: {} },
  radii: {},
  fonts: {},
  buttons: {},
  density: {},
  shadows: {},
  header: {},
  sections: {},
}

/**
 * Valide une forme reçue (réponse d'API, cache local) : tout ou rien. Une
 * partie illisible fait écarter l'ensemble — appliquer les couleurs d'une
 * réponse dont le reste est suspect, c'est déjà lui faire confiance.
 */
export function normaliserReglages(brut: unknown): ReglagesApparence | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const { colors, radii, fonts, buttons, density, shadows, header, sections } = brut as Record<
    string,
    unknown
  >
  const couleurs = normaliser(colors)
  const arrondis = normaliserArrondis(radii)
  const polices = normaliserPolices(fonts)
  const boutons = normaliserBoutons(buttons)
  const densite = normaliserDensite(density)
  const ombres = normaliserOmbres(shadows)
  const entete = normaliserEntete(header)
  const fondSections = normaliserSections(sections)
  if (
    couleurs === null ||
    arrondis === null ||
    polices === null ||
    boutons === null ||
    densite === null ||
    ombres === null ||
    entete === null ||
    fondSections === null
  ) {
    return null
  }
  return {
    colors: couleurs,
    radii: arrondis,
    fonts: polices,
    buttons: boutons,
    density: densite,
    shadows: ombres,
    header: entete,
    sections: fondSections,
  }
}

/** Feuille complète des surcharges, `''` s'il n'y en a aucune. */
export function feuilleComplete(reglages: ReglagesApparence): string {
  // Pas de `header` ni de `sections` ici, contrairement aux autres familles : la disposition de
  // la coquille est une STRUCTURE rendue par `AppLayout.vue` (via
  // `enteteAppliquee`), pas une feuille de style.
  return [
    feuilleDeStyle(reglages.colors),
    feuilleArrondis(reglages.radii),
    feuillePolices(reglages.fonts),
    feuilleBoutons(reglages.buttons),
    feuilleDensite(reglages.density),
    feuilleOmbres(reglages.shadows),
  ]
    .filter((bloc) => bloc !== '')
    .join('\n')
}
